import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { createI18nConfig } from '@/common/i18n/config';

import { readLocaleCookie, writeLocaleCookie } from '@/common/i18n/cookie';
import { isValidLanguageCode, DEFAULT_LANGUAGE_CODE } from '@/common/i18n/languages';
import AccountService from '@/client/service/account';

// Import English translation resources
import enSystem from '@/client/locales/en/system.json';
import enAuthentication from '@/client/locales/en/authentication.json';
import enRegistration from '@/client/locales/en/registration.json';
import enCalendars from '@/client/locales/en/calendars.json';
import enEditEvent from '@/client/locales/en/event_editor.json';
import enProfile from '@/client/locales/en/profile.json';
import enAdmin from '@/client/locales/en/admin.json';
import enInbox from '@/client/locales/en/inbox.json';
import enFeed from '@/client/locales/en/feed.json';
import enMedia from '@/client/locales/en/media.json';
import enCategories from '@/client/locales/en/categories.json';
import enSeries from '@/client/locales/en/series.json';
import enSetup from '@/client/locales/en/setup.json';
import enFunding from '@/client/locales/en/funding.json';
import enPolicy from '@/client/locales/en/policy.json';
import enSettings from '@/client/locales/en/settings.json';

// Import Spanish translation resources
import esSystem from '@/client/locales/es/system.json';
import esAuthentication from '@/client/locales/es/authentication.json';
import esRegistration from '@/client/locales/es/registration.json';
import esCalendars from '@/client/locales/es/calendars.json';
import esEditEvent from '@/client/locales/es/event_editor.json';
import esProfile from '@/client/locales/es/profile.json';
import esAdmin from '@/client/locales/es/admin.json';
import esInbox from '@/client/locales/es/inbox.json';
import esFeed from '@/client/locales/es/feed.json';
import esMedia from '@/client/locales/es/media.json';
import esCategories from '@/client/locales/es/categories.json';
import esSeries from '@/client/locales/es/series.json';
import esSetup from '@/client/locales/es/setup.json';
import esFunding from '@/client/locales/es/funding.json';
import esPolicy from '@/client/locales/es/policy.json';
import esSettings from '@/client/locales/es/settings.json';

// Import French translation resources
import frSystem from '@/client/locales/fr/system.json';
import frAuthentication from '@/client/locales/fr/authentication.json';
import frRegistration from '@/client/locales/fr/registration.json';
import frCalendars from '@/client/locales/fr/calendars.json';
import frEditEvent from '@/client/locales/fr/event_editor.json';
import frProfile from '@/client/locales/fr/profile.json';
import frAdmin from '@/client/locales/fr/admin.json';
import frInbox from '@/client/locales/fr/inbox.json';
import frFeed from '@/client/locales/fr/feed.json';
import frMedia from '@/client/locales/fr/media.json';
import frCategories from '@/client/locales/fr/categories.json';
import frSeries from '@/client/locales/fr/series.json';
import frSetup from '@/client/locales/fr/setup.json';
import frFunding from '@/client/locales/fr/funding.json';
import frPolicy from '@/client/locales/fr/policy.json';
import frSettings from '@/client/locales/fr/settings.json';

// Shared-component namespace owned by src/common/ui
import { uiResources } from '@/common/ui/locales';

/**
 * Detects the best language for the client from available signals.
 *
 * Detection chain (first valid match wins):
 *   1. pavilion_locale cookie
 *   2. Browser navigator.languages preference
 *   3. Instance default (serverLanguage parameter)
 *   4. Hard-coded 'en' fallback
 *
 * @param serverLanguage - Optional instance default language from server settings
 * @returns The best matching supported language code
 */
export function detectLanguage(serverLanguage?: string): string {
  // 1. Cookie preference
  const cookieLocale = readLocaleCookie();
  if (cookieLocale && isValidLanguageCode(cookieLocale)) {
    return cookieLocale;
  }

  // 2. Browser navigator.languages
  const browserLanguages = navigator.languages || (navigator.language ? [navigator.language] : []);
  for (const lang of browserLanguages) {
    // Try exact match first
    if (isValidLanguageCode(lang)) {
      return lang;
    }
    // Try base language (e.g. 'es' from 'es-MX')
    const baseLang = lang.split('-')[0];
    if (isValidLanguageCode(baseLang)) {
      return baseLang;
    }
  }

  // 3. Instance default
  if (serverLanguage && isValidLanguageCode(serverLanguage)) {
    return serverLanguage;
  }

  // 4. Hard-coded English fallback
  return DEFAULT_LANGUAGE_CODE;
}

/**
 * Initializes the i18next internationalization framework with all translation resources.
 * Uses the detection chain (cookie -> browser -> instance default -> 'en') to pick the
 * initial language.
 *
 * @param serverLanguage - Optional language code from server settings (e.g., 'es', 'en')
 * @returns {i18next.i18n} The configured i18next instance
 */
export const initI18Next = (serverLanguage?: string) => {
  const detectedLanguage = detectLanguage(serverLanguage);

  const config = createI18nConfig({
    lng: detectedLanguage,
    resources: {
      en: {
        system: enSystem,
        authentication: enAuthentication,
        registration: enRegistration,
        calendars: enCalendars,
        event_editor: enEditEvent,
        profile: enProfile,
        admin: enAdmin,
        inbox: enInbox,
        feed: enFeed,
        media: enMedia,
        categories: enCategories,
        series: enSeries,
        setup: enSetup,
        funding: enFunding,
        policy: enPolicy,
        settings: enSettings,
        ...uiResources.en,
      },
      es: {
        system: esSystem,
        authentication: esAuthentication,
        registration: esRegistration,
        calendars: esCalendars,
        event_editor: esEditEvent,
        profile: esProfile,
        admin: esAdmin,
        inbox: esInbox,
        feed: esFeed,
        media: esMedia,
        categories: esCategories,
        series: esSeries,
        setup: esSetup,
        funding: esFunding,
        policy: esPolicy,
        settings: esSettings,
        ...uiResources.es,
      },
      fr: {
        system: frSystem,
        authentication: frAuthentication,
        registration: frRegistration,
        calendars: frCalendars,
        event_editor: frEditEvent,
        profile: frProfile,
        admin: frAdmin,
        inbox: frInbox,
        feed: frFeed,
        media: frMedia,
        categories: frCategories,
        series: frSeries,
        setup: frSetup,
        funding: frFunding,
        policy: frPolicy,
        settings: frSettings,
        ...uiResources.fr,
      },
    },
  });

  i18next
    .use(LanguageDetector)
    .init(config);

  return i18next;
};

/**
 * Applies an account's saved language to the UI and writes the pavilion_locale
 * cookie so the next load in this browser starts in it.
 *
 * A missing or unsupported language means the account has no saved preference:
 * the cookie, browser and instance-default chain from `detectLanguage` stays
 * in charge, and no cookie is written.
 *
 * @param language - The account's saved language code, or null when unset
 */
export async function applyAccountLanguage(language: string | null | undefined): Promise<void> {
  if (!language || !isValidLanguageCode(language)) {
    return;
  }

  writeLocaleCookie(language);

  if (i18next.language === language) {
    return;
  }

  await i18next.changeLanguage(language);
}

/**
 * Reads the signed-in account's saved language and applies it.
 *
 * Called once when the client boots and once after a login, so a later
 * choice made in this browser holds until the next reload. Does nothing
 * without a signed-in session, so an anonymous visitor never gets the cookie.
 * A failed read leaves the detected language in place.
 *
 * @param authn - The session whose account language to apply
 * @param accountService - Service used to read the account's saved language
 */
export async function applySavedAccountLanguage(
  authn: { isLoggedIn(): boolean },
  accountService: AccountService = new AccountService(),
): Promise<void> {
  if (!authn.isLoggedIn()) {
    return;
  }

  try {
    await applyAccountLanguage(await accountService.getPreferredLanguage());
  }
  catch {
    // The UI stays in the detected language; nothing else depends on this.
  }
}

/**
 * Changes the active UI language and persists the choice.
 *
 * Steps (in order):
 *   1. Validate the language code — abort silently if invalid
 *   2. Change i18next language (updates all reactive translations)
 *   3. Write pavilion_locale cookie (persists for future visits)
 *   4. Call persistToApi callback if provided (stores preference server-side)
 *
 * @param language - The language code to switch to (e.g. 'es', 'en')
 * @param persistToApi - Optional async callback to persist the language to the server
 */
export async function changeLanguage(
  language: string,
  persistToApi?: (language: string) => Promise<void>,
): Promise<void> {
  if (!language || !isValidLanguageCode(language)) {
    return;
  }

  await i18next.changeLanguage(language);
  writeLocaleCookie(language);

  if (persistToApi) {
    await persistToApi(language);
  }
}
